import { VisitorInfo } from '../types';

// In-memory cache for the current session
let cachedVisitorInfo: VisitorInfo | null = null;

/**
 * Retorna un identificador seguro de sesión en el navegador (sin rastreo de hardware)
 */
export function getOrCreateVisitorId(): string {
  if (typeof window === 'undefined') return 'srv_visitor';
  try {
    let vid = sessionStorage.getItem('nuestros_recuerdos_vid');
    if (!vid) {
      const randomBytes = new Uint8Array(8);
      if (window.crypto && window.crypto.getRandomValues) {
        window.crypto.getRandomValues(randomBytes);
      }
      vid = Array.from(randomBytes).map((b) => b.toString(16).padStart(2, '0')).join('');
      sessionStorage.setItem('nuestros_recuerdos_vid', vid);
    }
    return `usr_${vid}`;
  } catch {
    return 'usr_guest';
  }
}

/**
 * Detección ética y no invasiva de categoría de dispositivo (Móvil, Tablet o Escritorio)
 * Sin recopilación de hardware, GPS ni datos privados.
 */
export async function detectRealDeviceSpecs(): Promise<{
  brand: string;
  model: string;
  os: string;
  osVersion: string;
  browser: string;
  browserVersion: string;
  deviceType: 'Móvil' | 'Escritorio' | 'Tablet';
  screenResolution: string;
  orientation: string;
}> {
  if (typeof window === 'undefined') {
    return {
      brand: 'Genérico',
      model: 'Dispositivo',
      os: 'Web',
      osVersion: '',
      browser: 'Navegador',
      browserVersion: '',
      deviceType: 'Escritorio',
      screenResolution: 'Estándar',
      orientation: 'Horizontal',
    };
  }

  const ua = navigator.userAgent || '';
  let deviceType: 'Móvil' | 'Escritorio' | 'Tablet' = 'Escritorio';
  if (/Tablet|iPad/i.test(ua)) {
    deviceType = 'Tablet';
  } else if (/Mobi|Android|iPhone/i.test(ua)) {
    deviceType = 'Móvil';
  }

  let os = 'Web';
  if (/Android/i.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'iOS';
  else if (/Windows/i.test(ua)) os = 'Windows';
  else if (/Mac OS X/i.test(ua)) os = 'macOS';
  else if (/Linux/i.test(ua)) os = 'Linux';

  let browser = 'Navegador';
  if (/Chrome/i.test(ua) && !/Edg|OPR/i.test(ua)) browser = 'Chrome';
  else if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) browser = 'Safari';
  else if (/Firefox/i.test(ua)) browser = 'Firefox';
  else if (/Edg/i.test(ua)) browser = 'Edge';

  return {
    brand: 'Dispositivo',
    model: deviceType,
    os,
    osVersion: '',
    browser,
    browserVersion: '',
    deviceType,
    screenResolution: 'Estándar',
    orientation: 'Auto',
  };
}

export interface GeoLocationResult {
  ip?: string;
  country?: string;
  countryCode?: string;
  city?: string;
  region?: string;
  flagEmoji?: string;
  latitude?: number;
  longitude?: number;
}

/**
 * Función segura sin GPS ni geolocalización invasiva.
 */
export async function fetchGeoLocation(): Promise<GeoLocationResult> {
  return {
    ip: 'IP Protegida',
    country: 'América',
    countryCode: 'CO',
    city: 'Bogotá',
    region: 'América',
    flagEmoji: '🌐',
    latitude: undefined,
    longitude: undefined,
  };
}

export function getCachedVisitorIp(): string | null {
  return 'IP Protegida';
}

export async function fetchFastPublicIp(): Promise<string> {
  return 'IP Protegida';
}

export async function fetchPublicIp(): Promise<string> {
  return 'IP Protegida';
}

export function getGpuInfo(): { renderer: string; vendor: string } {
  return { renderer: 'Estándar', vendor: 'Estándar' };
}

/**
 * Detección limpia de sesión para el conteo de visitas respetando 100% la privacidad:
 * - NO pide geolocalización (navigator.geolocation jamás se invoca)
 * - NO pide IP a servicios externos
 * - NO lee GPS, coordenadas, dirección ni hardware invasivo
 */
export async function detectVisitorInfo(): Promise<VisitorInfo> {
  if (cachedVisitorInfo) {
    return cachedVisitorInfo;
  }

  const visitorId = getOrCreateVisitorId();
  const specs = await detectRealDeviceSpecs();
  const cleanVisitorId = visitorId.replace(/[^a-zA-Z0-9]/g, '_');
  const docId = `dev_${cleanVisitorId.substring(0, 24)}`;

  const info: VisitorInfo = {
    ip: 'IP Protegida',
    visitorId,
    docId,
    deviceType: specs.deviceType,
    brand: specs.brand,
    model: specs.model,
    os: specs.os,
    browser: specs.browser,
    screenResolution: 'Estándar',
    language: typeof navigator !== 'undefined' ? navigator.language || 'es' : 'es',
    timezone: 'UTC',
    userAgent: '',
    locationPermissionStatus: 'denied',
    latitude: undefined,
    longitude: undefined,
  };

  cachedVisitorInfo = info;
  return info;
}
